import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fleetService } from '../../services/fleetService';
import { vehicleService } from '../../services/vehicleService';
import { reservationService } from '../../services/reservationService';
import { Vehicle } from '../../types/vehicle';
import { Reservation } from '../../types/reservation';
import VehicleSelector from '../../components/reservation/VehicleSelector';
import ReservationDetailsForm from '../../components/reservation/ReservationDetailsForm';
import ReservationSummary from '../../components/reservation/ReservationSummary';
import ReservationValidation from '../../components/reservation/ReservationValidation';
import ReservationConfirmation from '../../components/reservation/ReservationConfirmation';
import ReadOnlyCalendarModal from '../../components/reservation/ReadOnlyCalendarModal';
import { differenceInMinutes } from 'date-fns';

const PublicReservationPage: React.FC = () => {
  const { publicLinkId } = useParams<{ publicLinkId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fleet, setFleet] = useState<any | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedDates, setSelectedDates] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });
  // Détails de réservation collectés à l'étape 0
  const [reservationDetails, setReservationDetails] = useState<{
    email: string;
    startDate: Date;
    endDate: Date;
    purpose: string;
    notes: string;
  } | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [requiresValidation, setRequiresValidation] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [reservationComplete, setReservationComplete] = useState(false);
  const [creatingReservation, setCreatingReservation] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarVehicleId, setCalendarVehicleId] = useState<string | undefined>(undefined);

  const steps = [
    t('reservation:publicPage.steps.reservationDetails'), // Étape 0: Dates et informations
    t('reservation:publicPage.steps.selectVehicle'),      // Étape 1: Sélection du véhicule
    t('reservation:summary.title', 'Récapitulatif'),      // Étape 2: Validation finale
    requiresValidation ? t('reservation:publicPage.steps.validation') : t('reservation:publicPage.steps.confirmation') // Étape 3
  ];

  useEffect(() => {
    const fetchFleetData = async () => {
      if (!publicLinkId) {
        setError('Invalid link');
        setLoading(false);
        return;
      }

      try {
        // Récupérer les informations de la flotte
        const fleetData = await fleetService.getFleetByPublicLink(publicLinkId);
        
        if (!fleetData) {
          setError('Fleet not found or link is disabled');
          setLoading(false);
          return;
        }
        
        setFleet(fleetData);
        
        // Récupérer les véhicules disponibles
        const availableVehicles = await fleetService.getAvailableVehiclesInFleet(
          fleetData.id,
          new Date(),
          new Date(new Date().setDate(new Date().getDate() + 30)) // +30 jours
        );
        
        setVehicles(availableVehicles);
      } catch (error) {
        console.error('Error fetching fleet data:', error);
        setError(error instanceof Error ? error.message : String(error));
      } finally {
        setLoading(false);
      }
    };

    fetchFleetData();
  }, [publicLinkId]);

  // Handler pour la soumission des détails (étape 0)
  const handleDetailsSubmit = (details: {
    email: string;
    startDate: Date;
    endDate: Date;
    purpose: string;
    notes: string;
  }) => {
    setReservationDetails(details);
    setSelectedDates({ start: details.startDate, end: details.endDate });
    setUserEmail(details.email);
    handleNext();
  };

  // Handler pour la sélection du véhicule (étape 1) - passe à l'étape de validation
  const handleVehicleSelect = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    handleNext(); // Passe à l'étape 2 (récapitulatif)
  };

  // Handler pour la confirmation finale (étape 2) - crée la réservation
  const handleFinalConfirmation = async () => {
    if (!reservationDetails || !fleet || !selectedVehicle) return;

    setCreatingReservation(true);
    setError(null);

    try {
      // Vérifier si le domaine email est autorisé
      const isEmailValid = await reservationService.validateEmailDomain(reservationDetails.email, fleet.id);

      if (!isEmailValid) {
        setError(t('reservation:errors.emailDomainNotAllowed'));
        setCreatingReservation(false);
        return;
      }

      // Vérifier si le véhicule est disponible
      const isAvailable = await reservationService.checkVehicleAvailability(
        selectedVehicle.id,
        reservationDetails.startDate,
        reservationDetails.endDate
      );

      if (!isAvailable) {
        setError(t('reservation:errors.vehicleNotAvailable'));
        setCreatingReservation(false);
        return;
      }

      // Créer la réservation
      const reservationId = await reservationService.createReservation({
        organizationId: fleet.organizationId,
        fleetId: fleet.id,
        vehicleId: selectedVehicle.id,
        userEmail: reservationDetails.email,
        startDate: reservationDetails.startDate,
        endDate: reservationDetails.endDate,
        purpose: reservationDetails.purpose,
        notes: reservationDetails.notes
      });

      // Charger les détails de la réservation
      const reservationData = await reservationService.getReservation(reservationId);
      if (reservationData) {
        setReservation(reservationData);
      }
      setRequiresValidation(fleet.requireEmailValidation || false);
      handleNext(); // Passe à l'étape 3 (confirmation)
    } catch (error) {
      console.error('Error creating reservation:', error);
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setCreatingReservation(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleOpenCalendar = (vehicleId?: string) => {
    setCalendarVehicleId(vehicleId);
    setCalendarModalOpen(true);
  };

  const handleCloseCalendar = () => {
    setCalendarModalOpen(false);
    setCalendarVehicleId(undefined);
  };

  const handleApplyNextSlot = async (vehicleId: string) => {
    if (!reservationDetails) return;

    try {
      const duration = differenceInMinutes(reservationDetails.endDate, reservationDetails.startDate);
      const nextSlot = await reservationService.suggestNextAvailableSlot(
        vehicleId,
        reservationDetails.startDate,
        duration
      );

      if (nextSlot) {
        // Mettre à jour les dates dans reservationDetails
        setReservationDetails({
          ...reservationDetails,
          startDate: nextSlot.startDate,
          endDate: nextSlot.endDate
        });
        setSelectedDates({
          start: nextSlot.startDate,
          end: nextSlot.endDate
        });
        alert(t('reservation:publicPage.nextSlotFound', `Prochain créneau disponible: ${nextSlot.startDate.toLocaleString()}`));
      } else {
        alert(t('reservation:publicPage.noNextSlot', 'Aucun créneau disponible trouvé dans les 100 prochains jours.'));
      }
    } catch (error) {
      console.error('Error suggesting next slot:', error);
      alert(t('reservation:errors.nextSlotError', 'Erreur lors de la recherche du prochain créneau'));
    }
  };

  const handleModifyDates = () => {
    setActiveStep(0);
  };

  const handleNewReservation = () => {
    // Réinitialiser tout
    setActiveStep(0);
    setSelectedVehicle(null);
    setSelectedDates({ start: null, end: null });
    setReservationDetails(null);
    setReservation(null);
    setReservationComplete(false);
  };

  const handleValidationComplete = (success: boolean) => {
    if (success) {
      setReservationComplete(true);
      handleNext();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>
          {t('common.backToHome')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('reservation:publicPage.title')}
        </Typography>
        
        {fleet && (
          <Typography variant="subtitle1" color="text.secondary">
            {fleet.name}
          </Typography>
        )}
        
        <Stepper activeStep={activeStep} sx={{ mt: 3, mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* Étape 0: Sélection des dates et informations */}
        {activeStep === 0 && (
          <ReservationDetailsForm
            onDetailsSubmit={handleDetailsSubmit}
            onOpenCalendar={() => handleOpenCalendar()}
            initialEmail={reservationDetails?.email}
            initialStartDate={reservationDetails?.startDate}
            initialEndDate={reservationDetails?.endDate}
            initialPurpose={reservationDetails?.purpose}
            initialNotes={reservationDetails?.notes}
          />
        )}

        {/* Étape 1: Sélection du véhicule */}
        {activeStep === 1 && fleet && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <VehicleSelector
              vehicles={vehicles}
              loading={creatingReservation}
              onSelectVehicle={handleVehicleSelect}
              selectedVehicleId={selectedVehicle?.id}
              selectedStartDate={selectedDates.start}
              selectedEndDate={selectedDates.end}
              onModifyDates={handleModifyDates}
              onViewCalendar={handleOpenCalendar}
              onApplyNextSlot={handleApplyNextSlot}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={handleBack}>
                {t('common.back')}
              </Button>
            </Box>
          </>
        )}

        {/* Étape 2: Récapitulatif et validation finale */}
        {activeStep === 2 && reservationDetails && selectedVehicle && fleet && (
          <>
            <ReservationSummary
              reservationDetails={reservationDetails}
              vehicle={selectedVehicle}
              fleetName={fleet.name}
              onConfirm={handleFinalConfirmation}
              onEditDates={() => setActiveStep(0)}
              onEditVehicle={() => setActiveStep(1)}
              loading={creatingReservation}
              error={error}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={handleBack} disabled={creatingReservation}>
                {t('common.back')}
              </Button>
            </Box>
          </>
        )}

        {/* Étape 3: Confirmation finale */}
        {activeStep === 3 && reservation && selectedVehicle && fleet && (
          <>
            {requiresValidation && !reservationComplete ? (
              <ReservationValidation
                reservationId={reservation.id}
                email={userEmail}
                onValidationComplete={handleValidationComplete}
              />
            ) : (
              <ReservationConfirmation
                reservation={reservation}
                vehicle={selectedVehicle}
                fleet={fleet}
                onNewReservation={handleNewReservation}
              />
            )}
          </>
        )}
      </Paper>

      {/* Modal calendrier en lecture seule */}
      {fleet && (
        <ReadOnlyCalendarModal
          open={calendarModalOpen}
          onClose={handleCloseCalendar}
          fleetId={fleet.id}
          fleetName={fleet.name}
          vehicles={vehicles}
          selectedVehicleId={calendarVehicleId}
        />
      )}
    </Container>
  );
};

export default PublicReservationPage;
